import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useRef } from "react";

import {
  $populateEditorFromPlainText,
  $serializeEditorToPlainText,
} from "./lexicalTextSync";
import type { EditorState, LexicalEditor } from "lexical";

const SYNC_TAG = "lexical-plain-text-sync";

export interface UseLexicalPlainTextSyncOptions {
  value: string;
  onChange: (next: string) => void;
  /** When true, re-populate the editor whenever `value` changes externally. */
  controlled?: boolean;
}

/**
 * Shared plain-text sync loop for Lexical plain-text editors: populates the
 * editor on mount, serializes edits back out (guarded against its own updates
 * via {@link SYNC_TAG}), and — when `controlled` — re-populates on external
 * value changes. Returns the `onChange` handler for an `OnChangePlugin`.
 */
export function useLexicalPlainTextSync({
  value,
  onChange,
  controlled = false,
}: UseLexicalPlainTextSyncOptions) {
  const [editor] = useLexicalComposerContext();
  const lastExternalValueRef = useRef(value);
  const lastSerializedRef = useRef(value);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    editor.update(
      () => {
        $populateEditorFromPlainText(value);
      },
      { tag: SYNC_TAG },
    );
  }, [editor, value]);

  useEffect(() => {
    if (!controlled) return;
    if (!isInitialized.current) return;
    if (value === lastExternalValueRef.current) return;
    lastExternalValueRef.current = value;

    let editorAlreadyMatches = false;
    editor.read(() => {
      const current = $serializeEditorToPlainText();
      if (current === value) {
        editorAlreadyMatches = true;
        lastSerializedRef.current = value;
      }
    });

    if (editorAlreadyMatches) return;

    editor.update(
      () => {
        $populateEditorFromPlainText(value);
      },
      { tag: SYNC_TAG },
    );
    lastSerializedRef.current = value;
  }, [editor, value, controlled]);

  return useCallback(
    (
      _editorState: EditorState,
      latestEditor: LexicalEditor,
      tags: Set<string>,
    ) => {
      if (tags.has(SYNC_TAG)) return;

      latestEditor.read(() => {
        const serialized = $serializeEditorToPlainText();
        if (serialized !== lastSerializedRef.current) {
          lastSerializedRef.current = serialized;
          lastExternalValueRef.current = serialized;
          onChange(serialized);
        }
      });
    },
    [onChange],
  );
}
